import React, { useCallback, useState, useEffect } from 'react';
import momentLocalTimezone, {
  toMexicoCityInstant
} from 'src/utils/momentLocalTimezone';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  AppBar,
  Tabs,
  Tab,
  Typography,
  Button,
  Tooltip
} from '@mui/material';
import { FilterList } from '@mui/icons-material';
import FileDownloadIcon from '@mui/icons-material/GetApp';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import FadeIn from 'react-fade-in';
import clsx from 'clsx';
import Page from 'src/components/Page';
import Breadcrumb from 'src/components/Breadcrumb';
import Filter from 'src/components/Filter/Filter';
import TabPanel from 'src/components/TabPanel/TabPanel';
import ConfirmationDialog from 'src/components/ConfirmDialog/ConfirmationDialog';
import {
  loadSantanderPaymentReports,
  downloadAccumulatedSantanderReferencedPaymentsAction
} from 'src/store/actions/santanderClabeActions';
import {
  loadSantanderDomiciledCollectionReports,
  downloadSantanderDomiciledCollectionAction
} from 'src/store/actions/santanderDomiciledCollectionActions';
import { selectSantanderClabeQueryOptions } from 'src/store/selectors/santanderClabeSelectors';
import { selectSantanderDomiciledCollectionQueryOptions } from 'src/store/selectors/santanderDomiciledCollectionSelectors';
import FILTER_TYPES from 'src/utils/constants/filter';
import useStyles from './ReferencedPayments/styles';
import SantanderReportsList from './ReferencedPayments/SantanderReportsList';
import DomiciledCollectionReportsList from './DomiciledCollection/DomiciledCollectionReportsList';

const SANTANDER_REPORTS_TABS = {
  DOMICILED_COLLECTION: 'domiciled-collection',
  REFERENCED_PAYMENTS: 'referenced-payments'
};

const filters = [
  {
    title: 'COMMON.DATE',
    controlGroup: FILTER_TYPES.DATE_IN_RANGE,
    items: [
      {
        label: 'COMMON.START_DATE',
        value: 'startDate',
        view: ['year', 'month', 'day']
      },
      {
        label: 'COMMON.END_DATE',
        value: 'endDate',
        view: ['year', 'month', 'day']
      }
    ]
  }
];

function getFilterOptions(options) {
  const filterOptions = {};

  if (options.startDate) {
    filterOptions.startDate = toMexicoCityInstant(options.startDate).toDate();
  }
  if (options.endDate) {
    filterOptions.endDate = toMexicoCityInstant(options.endDate).toDate();
  }

  return filterOptions;
}

const ReportsView = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { tab } = useParams();
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeTab, setActiveTab] = useState(false);
  const [isDownloadDialogOpen, setIsDownloadDialogOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const queryOptions = useSelector(selectSantanderClabeQueryOptions);
  const domiciledCollectionQueryOptions = useSelector(
    selectSantanderDomiciledCollectionQueryOptions
  );
  const [filter, setFilter] = useState(getFilterOptions(queryOptions));

  const currentQueryOptions =
    activeTab === SANTANDER_REPORTS_TABS.REFERENCED_PAYMENTS
      ? queryOptions
      : domiciledCollectionQueryOptions;

  const startDateValue = filter.startDate || currentQueryOptions.startDate;
  const startDate = startDateValue
    ? momentLocalTimezone(startDateValue).toDate()
    : momentLocalTimezone()
        .startOf('isoWeek')
        .toDate();

  const endDateValue = filter.endDate || currentQueryOptions.endDate;
  const endDate = endDateValue
    ? momentLocalTimezone(endDateValue).toDate()
    : momentLocalTimezone()
        .endOf('isoWeek')
        .toDate();

  const handleTabChange = useCallback(
    (_, newValue) => {
      setActiveTab(newValue);
      navigate(`/santander-reports/${newValue}`);
    },
    [navigate]
  );

  useEffect(() => {
    if (!tab) {
      setActiveTab(SANTANDER_REPORTS_TABS.REFERENCED_PAYMENTS);
    }

    if (Object.values(SANTANDER_REPORTS_TABS).includes(tab)) {
      setActiveTab(tab);
    }
  }, [tab]);

  const handleClickFilter = useCallback(
    event => {
      setAnchorEl(event.currentTarget);
    },
    [setAnchorEl]
  );

  const handleCloseFilter = useCallback(() => {
    setAnchorEl(null);
  }, [setAnchorEl]);

  const handleFilterChange = useCallback(
    filterValues => {
      const filterOptions = getFilterOptions(filterValues);
      setFilter(filterOptions);
      if (activeTab === SANTANDER_REPORTS_TABS.REFERENCED_PAYMENTS) {
        dispatch(
          loadSantanderPaymentReports({ ...queryOptions, ...filterOptions })
        );
      } else if (activeTab === SANTANDER_REPORTS_TABS.DOMICILED_COLLECTION) {
        dispatch(
          loadSantanderDomiciledCollectionReports({
            ...domiciledCollectionQueryOptions,
            ...filterOptions
          })
        );
      }
    },
    [dispatch, queryOptions, domiciledCollectionQueryOptions, activeTab]
  );

  const handleDownloadClick = useCallback(() => {
    setIsDownloadDialogOpen(true);
  }, []);

  const handleConfirmDownload = useCallback(async () => {
    try {
      setIsDownloading(true);
      if (activeTab === SANTANDER_REPORTS_TABS.DOMICILED_COLLECTION) {
        await dispatch(
          downloadSantanderDomiciledCollectionAction({ startDate, endDate })
        );
      } else if (activeTab === SANTANDER_REPORTS_TABS.REFERENCED_PAYMENTS) {
        await dispatch(
          downloadAccumulatedSantanderReferencedPaymentsAction({
            startDate,
            endDate
          })
        );
      }
      setIsDownloading(false);
      setIsDownloadDialogOpen(false);
    } catch (error) {
      console.error(error);
      setIsDownloading(false);
    }
  }, [dispatch, activeTab, startDate, endDate]);

  const getDownloadMessage = () => {
    const startDateText = momentLocalTimezone(startDate).format('DD/MM/YYYY');
    const endDateText = momentLocalTimezone(endDate).format('DD/MM/YYYY');
    return t('NAVIGATION.DOWNLOAD_CONFIRMATION_MESSAGE', {
      startDate: startDateText,
      endDate: endDateText
    });
  };

  return (
    <Page className={classes.ContainerRoot} title={t('NAVIGATION.REPORTS')}>
      <FadeIn transitionDuration={200}>
        <Container maxWidth={false}>
          <Breadcrumb />
          <Box className={classes.actionsContainer}>
            <Typography
              style={{ fontWeight: '400', textTransform: 'capitalize' }}
              color="primary"
              variant="h1"
            >
              {t('NAVIGATION.REPORTS')}
            </Typography>
            <Box
              display="flex"
              justifyContent="flex-end"
              alignItems="space-between"
              gap={2}
            >
              <Tooltip title={t('COMMON.DOWNLOAD')}>
                <Button
                  className={clsx(classes.buttons)}
                  onClick={handleDownloadClick}
                  color="primary"
                  sx={{
                    backgroundColor: '#FFFFFF',
                    alignItems: 'center'
                  }}
                >
                  <FileDownloadIcon />
                </Button>
              </Tooltip>
              <Button
                className={clsx(
                  classes.buttons,
                  classes.horizontalPadding,
                  classes.backgroundColorWhite
                )}
                color="primary"
                onClick={handleClickFilter}
                sx={{ backgroundColor: '#FFFFFF' }}
              >
                <FilterList className={classes.icon} />
                {t('COMMON.FILTER')}
              </Button>
            </Box>
          </Box>
          <AppBar
            variant="outlined"
            position="static"
            sx={{
              border: 'none',
              borderBottom: '1px solid rgba(43,45,66,0.1)',
              background: 'transparent',
              flexDirection: 'row'
            }}
          >
            <Tabs
              indicatorColor="primary"
              textColor="primary"
              value={activeTab}
              onChange={handleTabChange}
            >
              <Tab
                label={t('NAVIGATION.REFERENCED_PAYMENTS')}
                value={SANTANDER_REPORTS_TABS.REFERENCED_PAYMENTS}
              />

              <Tab
                label={t('NAVIGATION.DOMICILED_COLLECTION')}
                value={SANTANDER_REPORTS_TABS.DOMICILED_COLLECTION}
              />
            </Tabs>
          </AppBar>

          <TabPanel
            value={activeTab}
            index={SANTANDER_REPORTS_TABS.REFERENCED_PAYMENTS}
          >
            <Box mt={3}>
              <SantanderReportsList />
            </Box>
          </TabPanel>

          <TabPanel
            value={activeTab}
            index={SANTANDER_REPORTS_TABS.DOMICILED_COLLECTION}
          >
            <DomiciledCollectionReportsList />
          </TabPanel>
          <Filter
            anchorEl={anchorEl}
            filterValues={
              activeTab === SANTANDER_REPORTS_TABS.REFERENCED_PAYMENTS
                ? queryOptions
                : domiciledCollectionQueryOptions
            }
            handleClose={handleCloseFilter}
            handleChange={handleFilterChange}
            filters={filters}
          />
        </Container>
      </FadeIn>
      <ConfirmationDialog
        open={isDownloadDialogOpen}
        handleClose={() => setIsDownloadDialogOpen(false)}
        onSubmit={handleConfirmDownload}
        title={t('COMMON.DOWNLOAD')}
        description={getDownloadMessage()}
        isLoading={isDownloading}
      />
    </Page>
  );
};

export default ReportsView;
